import { createEvent, createStore, sample } from 'effector';
import {
	createGetDashboardSeriesQuery,
	createGetDashboardSummaryQuery,
	DASHBOARD_PERIODS,
	DashboardPeriod,
	DashboardSummaryResponse,
} from 'shared/api';
import { TIME_ZONES } from 'shared/config';
import { authorizedDashboardRoute } from 'shared/routing';

const getDashboardSummaryQuery = createGetDashboardSummaryQuery();

const getDashboardSeriesQuery = createGetDashboardSeriesQuery();

const $period = createStore<DashboardPeriod>(DASHBOARD_PERIODS.last7Days);

const $timeZone = createStore(String(TIME_ZONES['UTC+07:00']));

const $currency = createStore('USD');

const setPeriod = createEvent<DashboardPeriod>();

const setTimeZone = createEvent<string>();

const setCurrency = createEvent<string>();

const setSelectedMetrics = createEvent<string[]>();

const $selectedMetrics = createStore<string[]>([]);

const $summary = createStore<DashboardSummaryResponse | null>(null);

$period.on(setPeriod, (_, period) => period);

$timeZone.on(setTimeZone, (_, timeZone) => timeZone);

$currency.on(setCurrency, (_, currency) => currency);

$selectedMetrics.on(setSelectedMetrics, (_, metrics) => metrics);

$summary.on(getDashboardSummaryQuery.finished.success, (_, { result }) => result);

sample({
	clock: authorizedDashboardRoute.opened,
	source: { currency: $currency, timeZone: $timeZone, period: $period },
	target: getDashboardSummaryQuery.start,
});

sample({
	clock: setPeriod,
	source: { timeZone: $timeZone, currency: $currency },
	fn: ({ timeZone, currency }, period) => ({ currency, timeZone, period }),
	target: getDashboardSummaryQuery.start,
});

sample({
	clock: setTimeZone,
	source: { period: $period, currency: $currency },
	fn: ({ period, currency }, timeZone) => ({ currency, timeZone, period }),
	target: getDashboardSummaryQuery.start,
});

sample({
	clock: setCurrency,
	source: { period: $period, timeZone: $timeZone },
	fn: ({ period, timeZone }, currency) => ({ currency, timeZone, period }),
	target: getDashboardSummaryQuery.start,
});

sample({
	clock: setSelectedMetrics,
	source: { currency: $currency, timeZone: $timeZone, period: $period },
	filter: (_, metrics) => metrics.length > 0,
	fn: ({ currency, timeZone, period }, metrics) => ({ currency, timeZone, period, metrics }),
	target: getDashboardSeriesQuery.start,
});

sample({
	clock: setSelectedMetrics,
	filter: (metrics) => metrics.length === 0,
	target: getDashboardSeriesQuery.reset,
});

sample({
	clock: setPeriod,
	source: { timeZone: $timeZone, currency: $currency, metrics: $selectedMetrics },
	filter: ({ metrics }) => metrics.length > 0,
	fn: ({ timeZone, currency, metrics }, period) => ({ currency, timeZone, period, metrics }),
	target: getDashboardSeriesQuery.start,
});

sample({
	clock: setTimeZone,
	source: { period: $period, currency: $currency, metrics: $selectedMetrics },
	filter: ({ metrics }) => metrics.length > 0,
	fn: ({ period, currency, metrics }, timeZone) => ({ currency, timeZone, period, metrics }),
	target: getDashboardSeriesQuery.start,
});

sample({
	clock: setCurrency,
	source: { period: $period, timeZone: $timeZone, metrics: $selectedMetrics },
	filter: ({ metrics }) => metrics.length > 0,
	fn: ({ period, timeZone, metrics }, currency) => ({ currency, timeZone, period, metrics }),
	target: getDashboardSeriesQuery.start,
});

sample({
	clock: authorizedDashboardRoute.closed,
	target: [
		getDashboardSummaryQuery.reset,
		getDashboardSeriesQuery.reset,
		$period.reinit,
		$timeZone.reinit,
		$currency.reinit,
		$selectedMetrics.reinit,
		$summary.reinit,
	],
});

export const dashboardPageModel = {
	getDashboardSummaryQuery,
	getDashboardSeriesQuery,
	$summary,
	$period,
	$timeZone,
	$currency,
	$selectedMetrics,
	setPeriod,
	setTimeZone,
	setCurrency,
	setSelectedMetrics,
};
